// CostSpaceLanding.jsx — CostSpace marketing landing page as a React component.
// Integrated into the CRA app as a standard ES module (`export default`,
// imported by App.jsx). Visual markup/CSS/copy are exactly as designed --
// only the CTA wiring and the previously-missing event-listener cleanup
// were added (see the comments inside the useEffect below).
//
// Accepts an optional `onStartTrial` prop, called when any "Start Free
// Trial" CTA is clicked (opens the Trial Request modal in App.jsx --
// this component has no knowledge of that modal, it only reports the
// click). Renders standalone (its original #pricing-anchor behavior)
// if no onStartTrial is passed.
import React from 'react'
import { supabase } from '../lib/supabase'

// Extra rules for the pricing cards that are built from the database (tiers table, custom text, loading note).
const pricingExtraCss = '.pc-tiers{margin-top:14px;display:flex;flex-direction:column;gap:8px}.pc-tier{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;padding:10px 12px;border:1px solid var(--line);border-radius:10px;font-size:13.5px}.pc-tier b{display:block;color:var(--ink);font-weight:600}.pc-tier small{display:block;color:var(--slate);font-size:12px;margin-top:2px}.pc-tier .t-price{font-weight:600;color:var(--ink);white-space:nowrap}.price-card .pc-price.pc-text{font-size:20px;line-height:1.3}.pc-loading{color:var(--slate);font-size:14px;padding:24px}';

const costspaceLandingCss = `
  :root{
    --blue:#0B3D91;
    --blue-2:#5DADE2;
    --accent:#EAF4FF;
    --ink:#0B1F3A;
    --ink-2:#0F2A45;
    --line-dk:#1A3A5C;
    --slate:#5A6478;
    --bg:#F5F8FD;
    --line:#E1E9F4;
    --white:#FFFFFF;
    --green:#2BB673;
    --amber:#E0A53B;
    --red:#C2384B;
    --sans:'IBM Plex Sans',sans-serif;
    --disp:'Space Grotesk',sans-serif;
    --mono:'IBM Plex Mono',monospace;
  }
  *{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  body{font-family:var(--sans);color:var(--ink);background:var(--white);-webkit-font-smoothing:antialiased;line-height:1.5}
  a{text-decoration:none;color:inherit}
  .wrap{max-width:1200px;margin:0 auto;padding:0 40px}
  @media(max-width:640px){.wrap{padding:0 22px}}

  .ey{font-family:var(--mono);font-size:12px;letter-spacing:.16em;text-transform:uppercase;color:var(--blue);font-weight:500}
  .ey.lt{color:var(--blue-2)}
  h1,h2,h3{font-family:var(--disp);letter-spacing:-.02em;line-height:1.06}
  .btn{font-family:var(--sans);font-size:15px;font-weight:600;border-radius:11px;padding:14px 24px;cursor:pointer;border:1.5px solid transparent;display:inline-flex;align-items:center;justify-content:center;gap:9px;transition:background .15s,border-color .15s,transform .05s,box-shadow .15s}
  .btn:active{transform:translateY(1px)}
  .btn-pri{background:var(--blue);color:#fff}
  .btn-pri:hover{background:#0a358a;box-shadow:0 8px 22px rgba(11,61,145,.28)}
  .btn-pri svg{transition:transform .15s}
  .btn-pri:hover svg{transform:translateX(3px)}
  .btn-ghost{background:var(--white);color:var(--ink);border-color:var(--line)}
  .btn-ghost:hover{border-color:var(--blue-2);background:#FAFCFF}
  .btn-light{background:rgba(255,255,255,.10);color:#fff;border-color:rgba(255,255,255,.22)}
  .btn-light:hover{background:rgba(255,255,255,.18)}

  /* ---------- NAV ---------- */
  header.nav{position:sticky;top:0;z-index:50;background:rgba(255,255,255,.86);backdrop-filter:blur(14px);border-bottom:1px solid var(--line)}
  .nav-in{display:flex;align-items:center;gap:34px;height:70px}
  .logo{display:flex;align-items:center;gap:11px}
  .logo .wm{font-family:var(--disp);font-weight:600;font-size:21px;letter-spacing:-.02em;color:var(--ink)}
  .logo .wm .b2{color:var(--blue)}
  .nav-links{display:flex;gap:30px;margin-left:8px}
  .nav-links a{font-size:14px;color:var(--slate);font-weight:500;transition:color .15s}
  .nav-links a:hover{color:var(--ink)}
  .nav-cta{margin-left:auto;display:flex;align-items:center;gap:12px}
  .nav-cta .btn{padding:10px 18px;font-size:14px;white-space:nowrap}
  @media(max-width:1100px){.nav-links{display:none}.nav-cta .ghost-hide{display:none}}

  /* ---------- HERO ---------- */
  .hero{position:relative;overflow:hidden;background:linear-gradient(180deg,#F8FBFF 0%,#FFFFFF 60%)}
  .hero::before{content:"";position:absolute;inset:0;opacity:.55;
    background-image:linear-gradient(rgba(93,173,226,.07) 1px,transparent 1px),linear-gradient(90deg,rgba(93,173,226,.07) 1px,transparent 1px);
    background-size:46px 46px;-webkit-mask-image:radial-gradient(ellipse 80% 70% at 50% 0%,#000 40%,transparent 80%);mask-image:radial-gradient(ellipse 80% 70% at 50% 0%,#000 40%,transparent 80%)}
  .hero-in{position:relative;display:flex;flex-direction:column;align-items:center;text-align:center;gap:48px;padding:88px 0 90px}
  .hero-copy{max-width:720px}
  .hero-cta,.hero-trust{justify-content:center}
  .dash-frame{width:100%;max-width:1080px}
  @media(max-width:980px){.hero-in{padding:54px 0 60px;gap:36px}}
  .pill{display:inline-flex;align-items:center;gap:9px;background:var(--white);border:1px solid var(--line);border-radius:999px;padding:7px 14px 7px 8px;font-size:12.5px;color:var(--slate);font-weight:500;margin-bottom:24px}
  .pill .tag{background:var(--accent);color:var(--blue);font-family:var(--mono);font-size:10.5px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;padding:3px 8px;border-radius:999px}
  .hero h1{font-size:58px;font-weight:600;color:var(--ink)}
  .hero h1 .b2{color:var(--blue)}
  @media(max-width:980px){.hero h1{font-size:44px}}
  @media(max-width:640px){.hero h1{font-size:34px}}
  .hero .lede{font-size:19px;color:var(--slate);max-width:540px;margin:22px 0 32px;text-wrap:pretty}
  .hero-cta{display:flex;gap:14px;flex-wrap:wrap}
  .hero-trust{display:flex;align-items:center;gap:22px;margin-top:30px;flex-wrap:wrap}
  .hero-trust .t{display:flex;align-items:center;gap:8px;font-size:13px;color:var(--slate)}
  .hero-trust .t .dot{width:6px;height:6px;border-radius:50%;background:var(--green)}

  /* ---------- DASHBOARD MOCKUP (hero) ---------- */
  .dash{background:var(--white);border:1px solid var(--line);border-radius:18px;box-shadow:0 30px 70px -28px rgba(11,31,58,.32);overflow:hidden}
  .dash .topbar{height:46px;display:flex;align-items:center;gap:8px;padding:0 16px;border-bottom:1px solid var(--line);background:#FBFDFF}
  .dash .topbar .dots{display:flex;gap:6px}
  .dash .topbar .dots span{width:10px;height:10px;border-radius:50%;background:#D9E2EF}
  .dash .topbar .crumb{font-family:var(--mono);font-size:11px;color:var(--slate);margin-left:10px}
  .dash .topbar .live{margin-left:auto;display:flex;align-items:center;gap:7px;font-family:var(--mono);font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:var(--green)}
  .dash .topbar .live .d{width:6px;height:6px;border-radius:50%;background:var(--green);box-shadow:0 0 0 0 rgba(43,182,115,.5);animation:pulse 2s infinite}
  @keyframes pulse{0%{box-shadow:0 0 0 0 rgba(43,182,115,.45)}70%{box-shadow:0 0 0 7px rgba(43,182,115,0)}100%{box-shadow:0 0 0 0 rgba(43,182,115,0)}}
  .dash .body{display:grid;grid-template-columns:160px 1fr;min-height:380px}
  .dash .side{background:var(--ink);padding:16px 12px;display:flex;flex-direction:column;gap:5px}
  .dash .side .sb-h{font-family:var(--mono);font-size:9px;letter-spacing:.12em;text-transform:uppercase;color:#5A7595;padding:6px 9px;margin-top:6px}
  .dash .side .it{display:flex;align-items:center;gap:9px;padding:8px 9px;border-radius:8px;font-size:12px;color:rgba(255,255,255,.55)}
  .dash .side .it .ic{width:13px;height:13px;border-radius:4px;background:rgba(255,255,255,.16)}
  .dash .side .it.on{background:rgba(93,173,226,.15);color:#fff;font-weight:500}
  .dash .side .it.on .ic{background:var(--blue-2)}
  .dash .main{padding:18px;display:flex;flex-direction:column;gap:14px;background:var(--bg)}
  .dash .kpis{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
  .dash .kpi{background:var(--white);border:1px solid var(--line);border-radius:11px;padding:13px 14px}
  .dash .kpi .k{font-family:var(--mono);font-size:9px;letter-spacing:.08em;text-transform:uppercase;color:var(--slate)}
  .dash .kpi .v{font-family:var(--disp);font-size:21px;font-weight:600;color:var(--ink);margin-top:7px}
  .dash .kpi .c{font-size:10.5px;font-weight:600;margin-top:5px;display:flex;align-items:center;gap:4px}
  .dash .kpi .c.up{color:var(--green)}
  .dash .kpi .c.dn{color:var(--red)}
  .dash .charts{display:grid;grid-template-columns:1.5fr 1fr;gap:12px;flex:1}
  .dash .card{background:var(--white);border:1px solid var(--line);border-radius:11px;padding:14px;display:flex;flex-direction:column}
  .dash .card .ch{font-family:var(--disp);font-size:12.5px;font-weight:600;color:var(--ink);margin-bottom:3px}
  .dash .card .cs{font-family:var(--mono);font-size:9.5px;color:var(--slate);margin-bottom:12px}
  /* bars */
  .bars{display:flex;align-items:flex-end;gap:10px;flex:1;min-height:108px}
  .bars .b{flex:1;display:flex;flex-direction:column;justify-content:flex-end;gap:3px;height:100%}
  .bars .b .stack{border-radius:4px 4px 0 0}
  .bars .b .planned{background:#D4E3F5}
  .bars .b .actual{background:var(--blue)}
  .bars .b .lbl{font-family:var(--mono);font-size:8px;color:var(--slate);text-align:center}
  /* donut */
  .donut-wrap{display:flex;align-items:center;gap:14px;flex:1}
  .donut{width:104px;height:104px;border-radius:50%;flex:none;
    background:conic-gradient(var(--blue) 0 62%,var(--blue-2) 62% 82%,#D4E3F5 82% 100%);
    -webkit-mask:radial-gradient(circle 32px at center,transparent 98%,#000 100%);mask:radial-gradient(circle 32px at center,transparent 98%,#000 100%);position:relative}
  .leg{display:flex;flex-direction:column;gap:9px}
  .leg .l{display:flex;align-items:center;gap:8px;font-size:11px;color:var(--slate)}
  .leg .l .sw{width:9px;height:9px;border-radius:3px}

  .float-card{position:absolute;background:var(--white);border:1px solid var(--line);border-radius:13px;box-shadow:0 18px 40px -16px rgba(11,31,58,.30);padding:13px 15px;display:flex;align-items:center;gap:11px;z-index:3}
  .float-card .fi{width:34px;height:34px;border-radius:9px;display:flex;align-items:center;justify-content:center;flex:none}
  .float-card .ft{font-family:var(--disp);font-size:14px;font-weight:600;color:var(--ink)}
  .float-card .fs{font-size:11px;color:var(--slate)}
  .fc-a{top:-22px;right:-18px}
  .fc-b{bottom:-24px;left:-22px}
  @media(max-width:980px){.fc-a{right:6px}.fc-b{left:6px}}
  @media(max-width:560px){.float-card{display:none}}
  .dash-frame{position:relative}

  /* ---------- LOGOS / TRUST STRIP ---------- */
  .strip{border-top:1px solid var(--line);border-bottom:1px solid var(--line);background:var(--bg)}
  .strip-in{padding:26px 0;display:flex;align-items:center;gap:38px;flex-wrap:wrap;justify-content:center}
  .strip .lab{font-family:var(--mono);font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:var(--slate)}
  .strip .item{font-family:var(--disp);font-weight:600;font-size:17px;color:#94A2B5;letter-spacing:-.01em}

  /* ---------- SECTION SHELL ---------- */
  section.sec{padding:96px 0}
  @media(max-width:640px){section.sec{padding:64px 0}}
  .sechead{max-width:680px;margin:0 auto 56px;text-align:center}
  .sechead.left{margin:0 0 48px;text-align:left}
  .sechead h2{font-size:40px;font-weight:600;color:var(--ink);margin:14px 0 0}
  @media(max-width:640px){.sechead h2{font-size:30px}}
  .sechead p{font-size:18px;color:var(--slate);margin-top:16px;text-wrap:pretty}

  /* ---------- PROBLEM ---------- */
  .prob{background:var(--ink);color:#fff;position:relative;overflow:hidden}
  .prob::before{content:"";position:absolute;inset:0;opacity:.5;
    background-image:linear-gradient(rgba(93,173,226,.06) 1px,transparent 1px),linear-gradient(90deg,rgba(93,173,226,.06) 1px,transparent 1px);background-size:42px 42px}
  .prob .sechead h2{color:#fff}
  .prob .sechead p{color:#9DB4CE}
  .prob .num-item{border-color:var(--line-dk)}
  .prob .num-item h4{color:#fff}
  .prob .num-item p{color:#8FA6C0}
  .prob-grid{position:relative;display:grid;grid-template-columns:repeat(4,1fr);gap:18px}
  @media(max-width:900px){.prob-grid{grid-template-columns:repeat(2,1fr)}}
  @media(max-width:520px){.prob-grid{grid-template-columns:1fr}}
  .prob-card{background:var(--ink-2);border:1px solid var(--line-dk);border-radius:16px;padding:24px}
  .prob-card .pn{font-family:var(--mono);font-size:11px;color:var(--blue-2);letter-spacing:.1em}
  .prob-card .ic{width:40px;height:40px;border-radius:10px;background:rgba(194,56,75,.14);border:1px solid rgba(194,56,75,.3);display:flex;align-items:center;justify-content:center;margin:14px 0 16px}
  .prob-card h3{font-size:17px;font-weight:600;color:#fff}
  .prob-card p{font-size:13.5px;color:#8FA6C0;margin-top:9px;line-height:1.55}
  .prob-quote{position:relative;text-align:center;max-width:820px;margin:60px auto 0}
  .prob-quote .q{font-family:var(--disp);font-size:30px;font-weight:600;letter-spacing:-.02em;color:#fff;line-height:1.25}
  @media(max-width:640px){.prob-quote .q{font-size:23px}}
  .prob-quote .q .hl{color:var(--blue-2)}

  /* ---------- SOLUTION ---------- */
  .sol-in{display:grid;grid-template-columns:1fr 1.05fr;gap:60px;align-items:center}
  @media(max-width:940px){.sol-in{grid-template-columns:1fr;gap:44px}}
  .sol-list{display:flex;flex-direction:column;gap:14px;margin-top:8px}
  .sol-item{display:flex;gap:16px;padding:20px;background:var(--white);border:1px solid var(--line);border-radius:14px;transition:border-color .15s,box-shadow .15s}
  .sol-item:hover{border-color:var(--blue-2);box-shadow:0 12px 30px -18px rgba(11,61,145,.28)}
  .sol-item .si{width:42px;height:42px;border-radius:11px;background:var(--accent);color:var(--blue);display:flex;align-items:center;justify-content:center;flex:none}
  .sol-item h3{font-size:16.5px;font-weight:600;color:var(--ink)}
  .sol-item p{font-size:14px;color:var(--slate);margin-top:5px}
  /* connected modules visual */
  .hub{position:relative;background:linear-gradient(160deg,#F8FBFF,#EEF5FF);border:1px solid var(--line);border-radius:20px;padding:36px;min-height:420px;display:flex;align-items:center;justify-content:center}
  .hub-core{position:relative;z-index:2;width:128px;height:128px;border-radius:24px;background:var(--blue);color:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;box-shadow:0 20px 44px -16px rgba(11,61,145,.55)}
  .hub-core .wm{font-family:var(--disp);font-weight:600;font-size:18px}
  .hub-core .wm .b2{color:var(--blue-2)}
  .hub-core .sub{font-family:var(--mono);font-size:8.5px;letter-spacing:.1em;text-transform:uppercase;color:rgba(255,255,255,.6)}
  .hub-ring{position:absolute;inset:0;display:flex;align-items:center;justify-content:center}
  .hub-node{position:absolute;background:var(--white);border:1px solid var(--line);border-radius:11px;padding:9px 13px;font-size:12px;font-weight:600;font-family:var(--disp);color:var(--ink);box-shadow:0 8px 20px -10px rgba(11,31,58,.22);display:flex;align-items:center;gap:7px}
  .hub-node .d{width:7px;height:7px;border-radius:50%;background:var(--blue-2)}
  .hub-node.n1{top:6%;left:50%;transform:translateX(-50%)}
  .hub-node.n2{top:26%;right:2%}
  .hub-node.n3{bottom:26%;right:2%}
  .hub-node.n4{bottom:6%;left:50%;transform:translateX(-50%)}
  .hub-node.n5{bottom:26%;left:2%}
  .hub-node.n6{top:26%;left:2%}
  @media(max-width:520px){.hub-node{font-size:10.5px;padding:7px 10px}}

  /* ---------- FEATURES ---------- */
  .feat-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}
  @media(max-width:900px){.feat-grid{grid-template-columns:repeat(2,1fr)}}
  @media(max-width:560px){.feat-grid{grid-template-columns:1fr}}
  .feat-card{background:var(--white);border:1px solid var(--line);border-radius:16px;padding:28px;transition:transform .15s,box-shadow .15s,border-color .15s}
  .feat-card:hover{transform:translateY(-3px);box-shadow:0 22px 44px -24px rgba(11,31,58,.28);border-color:var(--blue-2)}
  .feat-card .fi{width:46px;height:46px;border-radius:12px;background:var(--accent);color:var(--blue);display:flex;align-items:center;justify-content:center;margin-bottom:20px}
  .feat-card h3{font-size:18px;font-weight:600;color:var(--ink)}
  .feat-card p{font-size:14.5px;color:var(--slate);margin-top:9px;line-height:1.55}
  .feat-card .fn{font-family:var(--mono);font-size:11px;color:var(--blue-2);letter-spacing:.08em;float:right}

  /* ---------- SCREENSHOTS ---------- */
  .shots{background:var(--bg)}
  .shot-tabs{display:flex;gap:8px;justify-content:center;margin-bottom:34px;flex-wrap:wrap}
  .shot-tab{font-family:var(--sans);font-size:14px;font-weight:600;color:var(--slate);background:var(--white);border:1px solid var(--line);border-radius:10px;padding:11px 20px;cursor:pointer;transition:all .15s}
  .shot-tab.on{background:var(--blue);color:#fff;border-color:var(--blue)}
  .shot-stage{background:var(--white);border:1px solid var(--line);border-radius:18px;box-shadow:0 30px 70px -34px rgba(11,31,58,.30);overflow:hidden}
  .shot{display:none}
  .shot.on{display:block}

  /* generic app chrome for screenshots */
  .app{display:grid;grid-template-columns:188px 1fr;min-height:480px}
  @media(max-width:720px){.app{grid-template-columns:1fr}.app .aside{display:none}}
  .app .aside{background:var(--ink);padding:18px 14px;display:flex;flex-direction:column;gap:5px}
  .app .aside .brand{display:flex;align-items:center;gap:9px;padding:4px 8px 16px}
  .app .aside .brand .wm{font-family:var(--disp);font-weight:600;font-size:16px;color:#fff}
  .app .aside .brand .wm .b2{color:var(--blue-2)}
  .app .aside .it{display:flex;align-items:center;gap:10px;padding:9px 10px;border-radius:8px;font-size:12.5px;color:rgba(255,255,255,.55)}
  .app .aside .it .ic{width:14px;height:14px;border-radius:4px;background:rgba(255,255,255,.16)}
  .app .aside .it.on{background:rgba(93,173,226,.15);color:#fff;font-weight:500}
  .app .aside .it.on .ic{background:var(--blue-2)}
  .app .view{display:flex;flex-direction:column;background:var(--bg)}
  .app .vbar{height:56px;border-bottom:1px solid var(--line);background:var(--white);display:flex;align-items:center;padding:0 22px;gap:12px}
  .app .vbar .vt{font-family:var(--disp);font-weight:600;font-size:16px;color:var(--ink)}
  .app .vbar .vtag{font-family:var(--mono);font-size:10px;color:var(--slate);background:var(--bg);border:1px solid var(--line);padding:4px 9px;border-radius:6px}
  .app .vbar .av{margin-left:auto;width:30px;height:30px;border-radius:50%;background:var(--blue);color:#fff;font-size:11px;font-weight:600;display:flex;align-items:center;justify-content:center}
  .app .vbody{padding:22px;display:flex;flex-direction:column;gap:16px;flex:1}

  /* table mock */
  .tbl{background:var(--white);border:1px solid var(--line);border-radius:12px;overflow:hidden}
  .tbl .tr{display:grid;grid-template-columns:1.6fr 1fr 1fr 1fr 90px;gap:10px;padding:13px 18px;align-items:center;border-bottom:1px solid var(--line);font-size:12.5px}
  .tbl .tr:last-child{border-bottom:none}
  .tbl .tr.h{background:var(--bg);font-family:var(--mono);font-size:10px;letter-spacing:.06em;text-transform:uppercase;color:var(--slate)}
  .tbl .tr .nm{font-weight:600;color:var(--ink)}
  .tbl .tr .mono{font-family:var(--mono);font-size:12px;color:var(--ink)}
  .tag-st{font-family:var(--mono);font-size:9.5px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;padding:4px 9px;border-radius:999px;display:inline-block}
  .st-ok{background:rgba(43,182,115,.12);color:#1c8f5a}
  .st-warn{background:rgba(224,165,59,.14);color:#a9781c}
  .st-risk{background:rgba(194,56,75,.12);color:#a52a3c}
  .prog{height:7px;border-radius:99px;background:var(--line);overflow:hidden}
  .prog i{display:block;height:100%;border-radius:99px;background:var(--blue)}

  /* ---------- USERS ---------- */
  .user-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:18px}
  @media(max-width:900px){.user-grid{grid-template-columns:repeat(2,1fr)}}
  @media(max-width:520px){.user-grid{grid-template-columns:1fr}}
  .user-card{border:1px solid var(--line);border-radius:16px;padding:26px;background:var(--white)}
  .user-card .ui{width:44px;height:44px;border-radius:11px;background:var(--ink);color:var(--blue-2);display:flex;align-items:center;justify-content:center;margin-bottom:18px}
  .user-card h3{font-size:16px;font-weight:600;color:var(--ink)}
  .user-card p{font-size:13.5px;color:var(--slate);margin-top:8px;line-height:1.55}

  /* ---------- ELEGANT PERSONA LIST ---------- */
  .persona-list{display:flex;flex-direction:column}
  .persona-item{display:grid;grid-template-columns:64px 1fr;gap:22px;align-items:flex-start;padding:26px 0;border-top:1px solid var(--line)}
  .persona-list .persona-item:last-child{border-bottom:1px solid var(--line)}
  .persona-item .pi-n{font-family:var(--mono);font-size:12px;color:var(--blue-2);letter-spacing:.08em;padding-top:2px}
  .persona-item h3{font-size:18px;font-weight:600;color:var(--ink)}
  .persona-item p{font-size:14.5px;color:var(--slate);margin-top:6px;max-width:560px}
  @media(max-width:560px){.persona-item{grid-template-columns:1fr}.persona-item .pi-n{display:none}}

  /* ---------- LIFECYCLE FLOW ---------- */
  .flow-wrap{display:flex;align-items:center;justify-content:center;flex-wrap:wrap;gap:0;margin-top:8px}
  .flow-step{display:flex;flex-direction:column;align-items:center;gap:8px;padding:20px 22px;background:var(--white);border:1px solid var(--line);border-radius:14px;min-width:128px;text-align:center}
  .flow-step .fn{font-family:var(--mono);font-size:10px;color:var(--blue-2);letter-spacing:.1em;text-transform:uppercase}
  .flow-step h4{font-family:var(--disp);font-size:15px;font-weight:600;color:var(--ink)}
  .flow-arrow{color:var(--line);padding:0 8px;flex:none}
  .flow-arrow svg{display:block}
  @media(max-width:900px){.flow-wrap{gap:10px}.flow-arrow{transform:rotate(90deg)}}
  .flow-card{background:var(--ink);border-radius:18px;padding:30px;margin-top:40px;display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
  @media(max-width:760px){.flow-card{grid-template-columns:repeat(2,1fr)}}
  .flow-card .fc-i{border-left:1px solid rgba(255,255,255,.14);padding-left:16px}
  .flow-card .fc-i:first-child{border-left:none;padding-left:0}
  @media(max-width:760px){.flow-card .fc-i:nth-child(odd){border-left:none;padding-left:0}}
  .flow-card .fc-k{font-family:var(--mono);font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:#7FA0C4}
  .flow-card .fc-v{font-family:var(--disp);font-size:22px;font-weight:600;color:#fff;margin-top:8px}
  .flow-card .fc-s{font-size:11.5px;color:#8FA6C0;margin-top:5px}

  /* ---------- PRICING ---------- */
  .pricing-grid{display:flex;flex-wrap:wrap;justify-content:center;gap:24px}
  .price-card{background:var(--white);border:1px solid var(--line);border-radius:18px;padding:34px;width:320px;position:relative}
  .price-card.hl{border:2px solid var(--blue);box-shadow:0 26px 56px -28px rgba(11,61,145,.32)}
  .price-card .pc-badge{position:absolute;top:-13px;left:50%;transform:translateX(-50%);background:var(--blue);color:#fff;font-family:var(--mono);font-size:10.5px;font-weight:600;letter-spacing:.08em;padding:5px 14px;border-radius:999px;text-transform:uppercase}
  .price-card .pc-name{font-family:var(--mono);font-size:12px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:var(--slate)}
  .price-card .pc-price{font-family:var(--disp);font-size:34px;font-weight:600;color:var(--ink);margin-top:10px}
  .price-card .pc-tag{font-size:13.5px;color:var(--slate);margin-top:8px}
  .price-card ul{list-style:none;margin:24px 0 28px;display:flex;flex-direction:column;gap:11px}
  .price-card li{display:flex;gap:9px;font-size:13.5px;color:var(--ink)}
  .price-card li .ck{color:var(--blue);font-weight:700;flex:none}

  /* ---------- CATALOGUE ---------- */
  .cat-card{background:linear-gradient(160deg,#F8FBFF,#EEF5FF);border:1px solid var(--line);border-radius:20px;padding:56px 40px;text-align:center;max-width:720px;margin:0 auto}
  .cat-lock{width:52px;height:52px;border-radius:14px;background:var(--ink);color:var(--blue-2);display:flex;align-items:center;justify-content:center;margin:0 auto 22px}

  /* ---------- FAQ ---------- */
  .faq{max-width:760px;margin:0 auto}
  .faq-item{border-bottom:1px solid var(--line)}
  .faq-item button{width:100%;display:flex;justify-content:space-between;align-items:center;gap:14px;background:none;border:none;cursor:pointer;text-align:left;padding:22px 0;font-family:var(--disp);font-size:16.5px;font-weight:600;color:var(--ink)}
  .faq-item .fq-ic{font-size:20px;color:var(--slate);flex:none}
  .faq-item .fq-a{font-size:14.5px;color:var(--slate);line-height:1.65;max-width:640px;display:none;padding:0 0 22px}
  .faq-item.open .fq-a{display:block}
  .faq-item.open .fq-ic{color:var(--blue)}

  /* ---------- NUMBERED LIST + COMPARISON (new composition) ---------- */
  .num-list{display:flex;flex-direction:column}
  .num-item{display:grid;grid-template-columns:40px 1fr;gap:16px;padding:20px 0;border-top:1px solid var(--line)}
  .num-list .num-item:last-child{border-bottom:1px solid var(--line)}
  .num-item .nn{font-family:var(--mono);font-size:12px;color:var(--blue-2);padding-top:2px}
  .num-item h4{font-size:16px;font-weight:600;color:var(--ink)}
  .num-item p{font-size:13.5px;color:var(--slate);margin-top:5px;line-height:1.55}
  .cmp-wrap{border:1px solid var(--line);border-radius:16px;overflow:hidden;background:var(--white)}
  .cmp-row{display:grid;grid-template-columns:1.3fr 1fr 1fr;align-items:center}
  .cmp-row.h{background:var(--ink)}
  .cmp-row.h div{color:#fff;font-family:var(--disp);font-size:13.5px;font-weight:600;padding:16px 18px}
  .cmp-row.h div.hl{background:var(--blue)}
  .cmp-row:not(.h){border-top:1px solid var(--line)}
  .cmp-row div{padding:14px 18px;font-size:13px;color:var(--slate)}
  .cmp-row .lbl{color:var(--ink);font-weight:500}
  .cmp-row .yes{color:var(--blue);font-weight:700}
  .cmp-row .no{color:#C7CEDA}
  .cmp-row div.hl-cell{background:var(--accent);color:var(--ink);font-weight:600}
  @media(max-width:640px){.cmp-row{grid-template-columns:1.2fr .9fr .9fr}.cmp-row div{padding:12px 10px;font-size:12px}}

  /* ---------- FEATURE ROWS (replaces card-grid pattern) ---------- */
  .feat-rows{display:grid;grid-template-columns:1fr 1fr;gap:0 48px}
  @media(max-width:760px){.feat-rows{grid-template-columns:1fr}}
  .feat-row{display:grid;grid-template-columns:44px 1fr;gap:16px;padding:22px 0;border-top:1px solid var(--line)}
  .feat-rows .feat-row:nth-last-child(-n+2){border-bottom:1px solid var(--line)}
  @media(max-width:760px){.feat-rows .feat-row:nth-last-child(-n+2){border-bottom:none}.feat-rows .feat-row:last-child{border-bottom:1px solid var(--line)}}
  .feat-row .fi{width:44px;height:44px;border-radius:11px;background:var(--accent);color:var(--blue);display:flex;align-items:center;justify-content:center;flex:none}
  .feat-row h3{font-size:16.5px;font-weight:600;color:var(--ink)}
  .feat-row p{font-size:13.5px;color:var(--slate);margin-top:6px;line-height:1.55}

  /* ---------- 3-STEP ---------- */
  .steps3{display:grid;grid-template-columns:repeat(3,1fr);gap:28px}
  @media(max-width:760px){.steps3{grid-template-columns:1fr}}
  .step3{text-align:center;padding:0 12px}
  .step3 .s3n{width:40px;height:40px;border-radius:50%;background:var(--ink);color:#fff;font-family:var(--disp);font-weight:600;font-size:15px;display:flex;align-items:center;justify-content:center;margin:0 auto 18px}
  .step3 h4{font-size:17px;font-weight:600;color:var(--ink)}
  .step3 p{font-size:13.5px;color:var(--slate);margin-top:8px;max-width:260px;margin-left:auto;margin-right:auto}

  /* ---------- TESTIMONIAL (big quote) ---------- */
  .quote-big{max-width:760px;margin:0 auto;text-align:center}
  .quote-big .q{font-family:var(--disp);font-size:30px;font-weight:500;letter-spacing:-.015em;color:var(--ink);line-height:1.35;text-wrap:pretty}
  @media(max-width:640px){.quote-big .q{font-size:22px}}
  .quote-big .attr{display:flex;align-items:center;justify-content:center;gap:12px;margin-top:26px}
  .quote-big .attr .av2{width:40px;height:40px;border-radius:50%;background:var(--ink);color:var(--blue-2);display:flex;align-items:center;justify-content:center;font-family:var(--disp);font-size:14px;font-weight:600}
  .quote-big .attr .nm{font-size:14px;font-weight:600;color:var(--ink)}
  .quote-big .attr .rl{font-size:12.5px;color:var(--slate)}

  /* ---------- FOUNDER ---------- */
  .founder{background:var(--bg)}

  /* ---------- CTA / FORM ---------- */
  .cta{background:var(--ink);color:#fff;position:relative;overflow:hidden}
  .cta::before{content:"";position:absolute;inset:0;opacity:.55;background-image:linear-gradient(rgba(93,173,226,.07) 1px,transparent 1px),linear-gradient(90deg,rgba(93,173,226,.07) 1px,transparent 1px);background-size:44px 44px;-webkit-mask-image:radial-gradient(ellipse 70% 80% at 70% 50%,#000,transparent 75%);mask-image:radial-gradient(ellipse 70% 80% at 70% 50%,#000,transparent 75%)}
  .cta-in{position:relative;display:grid;grid-template-columns:1fr 1fr;gap:60px;align-items:center}
  @media(max-width:880px){.cta-in{grid-template-columns:1fr;gap:40px}}
  .cta h2{font-size:42px;font-weight:600;color:#fff;letter-spacing:-.025em;line-height:1.08}
  @media(max-width:640px){.cta h2{font-size:32px}}
  .cta .lede{font-size:17px;color:#9DB4CE;margin-top:18px;max-width:440px;text-wrap:pretty}
  .cta-bullets{display:flex;flex-direction:column;gap:13px;margin-top:28px}
  .cta-bullets .cb{display:flex;align-items:center;gap:11px;font-size:14.5px;color:#C6D6E8}
  .cta-bullets .cb .ck{width:22px;height:22px;border-radius:6px;background:rgba(93,173,226,.16);color:var(--blue-2);display:flex;align-items:center;justify-content:center;flex:none}
  .form-card{background:var(--white);border-radius:18px;padding:34px;box-shadow:0 40px 90px -30px rgba(0,0,0,.5)}
  .form-card h3{font-family:var(--disp);font-size:21px;font-weight:600;color:var(--ink)}
  .form-card .fsub{font-size:13.5px;color:var(--slate);margin-top:6px;margin-bottom:22px}
  .form-card form{display:flex;flex-direction:column;gap:16px}
  .field label{font-family:var(--mono);font-size:11px;font-weight:500;letter-spacing:.1em;text-transform:uppercase;color:var(--slate);display:block;margin-bottom:7px}
  .field input,.field select{width:100%;padding:12px 14px;background:var(--white);border:1.5px solid var(--line);border-radius:10px;font-family:var(--sans);font-size:14.5px;color:var(--ink);outline:none;transition:border-color .15s,box-shadow .15s}
  .field input::placeholder{color:#A9B6C9}
  .field input:focus,.field select:focus{border-color:var(--blue-2);box-shadow:0 0 0 3px rgba(93,173,226,.18)}
  .form-row{display:grid;grid-template-columns:1fr 1fr;gap:14px}
  @media(max-width:420px){.form-row{grid-template-columns:1fr}}
  .form-card .submit{margin-top:4px}
  .form-card .fine{font-size:11.5px;color:#9AA6B8;text-align:center;margin-top:4px}
  .form-success{display:none;text-align:center;padding:20px 0}
  .form-success.on{display:block}
  .form-success .sc{width:54px;height:54px;border-radius:14px;background:rgba(43,182,115,.12);color:var(--green);display:flex;align-items:center;justify-content:center;margin:0 auto 16px}
  .form-success h3{margin-bottom:8px}
  .form-success p{font-size:14px;color:var(--slate)}

  /* ---------- FOOTER ---------- */
  footer.ft{background:var(--white);border-top:1px solid var(--line)}
  .ft-in{padding:50px 0 30px;display:grid;grid-template-columns:1.2fr 1fr 1fr 1fr 1.1fr;gap:30px}
  @media(max-width:900px){.ft-in{grid-template-columns:1fr 1fr}}
  .ft-brand .wm{font-family:var(--disp);font-weight:600;font-size:19px;color:var(--ink);margin-top:14px}
  .ft-brand .wm .b2{color:var(--blue)}
  .ft-brand p{font-size:13.5px;color:var(--slate);margin-top:12px;max-width:280px;line-height:1.6}
  .ft-col h4{font-family:var(--mono);font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:var(--slate);margin-bottom:16px}
  .ft-col a{display:block;font-size:13.5px;color:var(--ink);margin-bottom:11px;transition:color .15s}
  .ft-col a:hover{color:var(--blue)}
  .ft-bot{border-top:1px solid var(--line);padding:22px 0;display:flex;align-items:center;justify-content:space-between;gap:14px;flex-wrap:wrap}
  .ft-bot .c{font-size:12.5px;color:var(--slate)}
  .ft-bot .badge{font-family:var(--mono);font-size:11px;color:var(--blue);background:var(--accent);padding:5px 11px;border-radius:999px;letter-spacing:.06em}
  .ft-qr-link{display:inline-flex;flex-direction:column;align-items:flex-start;gap:8px;margin-top:6px}
  .ft-qr{width:100px;height:100px;border-radius:10px;border:1px solid var(--line);padding:6px;background:var(--white)}
  .ft-qr-cap{font-family:var(--mono);font-size:11px;color:var(--slate);letter-spacing:.04em}
  .cta-contact{margin-top:26px;padding-top:22px;border-top:1px solid var(--line)}
  .cc-lbl{display:block;font-family:var(--mono);font-size:11px;letter-spacing:.08em;text-transform:uppercase;color:var(--slate);margin-bottom:12px}
  .cc-row{display:flex;flex-wrap:wrap;gap:10px 22px}
  .cc-row a{font-size:13.5px;color:var(--ink);white-space:nowrap;transition:color .15s}
  .cc-row a:hover{color:var(--blue)}
  .nav-contact{position:relative}
  .nc-pop{position:absolute;top:calc(100% + 10px);right:0;background:var(--white);border:1px solid var(--line);border-radius:14px;padding:16px;box-shadow:0 16px 40px rgba(11,61,145,.12);display:none;flex-direction:column;align-items:center;width:220px;z-index:50}
  .nc-pop.open{display:flex}
  .nc-qr{width:96px;height:96px;border-radius:8px;border:1px solid var(--line);padding:5px;background:var(--white)}
  .nc-cap{font-family:var(--mono);font-size:10.5px;color:var(--slate);letter-spacing:.05em;margin-top:8px}
  .nc-links{display:flex;flex-direction:column;gap:8px;margin-top:12px;width:100%}
  .nc-links a{font-size:13px;color:var(--ink);text-align:center;transition:color .15s}
  .nc-links a:hover{color:var(--blue)}

  /* reveal */
  .rv{opacity:0;transform:translateY(22px);transition:opacity .6s ease,transform .6s ease}
  .rv.in{opacity:1;transform:none}
  @media(prefers-reduced-motion:reduce){.rv{opacity:1;transform:none;transition:none}}

  /* ---------- PRINT / PDF ---------- */
  @media print{
    *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
    @page{margin:0}
    html{scroll-behavior:auto}
    header.nav{position:static}
    .rv{opacity:1!important;transform:none!important;transition:none!important}
    .float-card .fi .d,.dash .topbar .live .d{animation:none!important}
    section.sec{padding:54px 0;break-inside:avoid}
    .hero-in{padding:48px 0}
    .prob-card,.feat-card,.sol-item,.user-card,.shot-stage,.dash,.form-card,.founder-portrait,.prob-quote{break-inside:avoid}
    .feat-grid,.prob-grid,.user-grid,.sol-in,.cta-in,.founder-in,.hero-in{break-inside:avoid}
    /* show all three product screenshots stacked instead of just the active tab */
    .shot{display:block!important;margin-bottom:18px}
    .shot-tabs{display:none!important}
  }
`;

function CostSpaceLanding({ onStartTrial, onLogin } = {}){
  const rootRef = React.useRef(null);

  React.useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.textContent = costspaceLandingCss + pricingExtraCss;
    document.head.appendChild(styleEl);

    // ---- behavior ported from the static page (pricing/FAQ render, tabs, nav popover, scroll reveal) ----
    if (window.__resources && window.__resources.qrCode && rootRef.current) {
      rootRef.current.querySelectorAll('img[src*="qrserver"]').forEach(function(img){ img.src = window.__resources.qrCode; });
    }
    
      // ====================================================================
      //  PRICING -- driven from the database (Subscription Management >
      //  Pricing) through the public, read-only get_landing_pricing().
      //  FALLBACK_PRICING is used ONLY if that call fails or times out, so
      //  the section is never empty.
      var cancelled = false;
      var FALLBACK_PRICING = {
        trial_days: 30,
        plans: [
          { code:'costspace', name:'CostSpace', tagline:'For active construction projects', cta_label:'Start Free Trial', highlighted:true, badge_text:null,
            features:['Everything in the free trial','Unlimited projects','Full Estimation module','Priority support'],
            currency:'EGP', billing_interval:'monthly', display_mode:'fixed', monthly_price:1000 }
        ]
      };
      var TRIAL_FEATURES = ['Full platform access during trial','Budget, Procurement &amp; Cost Control','Forecasting &amp; Reporting'];
      function esc(s){ return String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }
      function perLabel(interval){ return interval === 'monthly' ? 'month' : interval === 'yearly' ? 'year' : (interval || 'month'); }
      function money(cur, n, interval){ return esc(cur) + ' ' + Number(n).toLocaleString() + ' / ' + esc(perLabel(interval)); }
      function trialLabel(days){
        var d = Number(days) || 30;
        if (d % 30 === 0){ var m = d / 30; return m + (m === 1 ? ' month' : ' months'); }
        return d + (d === 1 ? ' day' : ' days');
      }
      function limitsText(t){
        var parts = [];
        if (t.max_users != null) parts.push('up to ' + t.max_users + ' users');
        if (t.max_projects != null) parts.push('up to ' + t.max_projects + ' projects');
        return parts.length ? parts.join(' \u00b7 ') : 'no limits';
      }
      function priceBlock(p){
        switch (p.display_mode){
          case 'fixed': return p.monthly_price != null ? '<div class="pc-price">' + money(p.currency, p.monthly_price, p.billing_interval) + '</div>' : '';
          case 'from_price': return p.from_price != null ? '<div class="pc-price">From ' + money(p.currency, p.from_price, p.billing_interval) + '</div>' : '';
          case 'custom_text': return p.custom_text ? '<div class="pc-price pc-text">' + esc(p.custom_text) + '</div>' : '';
          case 'tiers_table': return '<div class="pc-tiers">' + (p.tiers || []).map(function(t){
              return '<div class="pc-tier"><div><b>' + esc(t.name) + '</b><small>' + limitsText(t) + '</small></div><div class="t-price">' + money(p.currency, t.monthly_price, p.billing_interval) + '</div></div>';
            }).join('') + '</div>';
          default: return '';
        }
      }
      function cardHtml(c){
        return '<div class="price-card' + (c.highlighted ? ' hl' : '') + '">' +
          (c.badge ? '<div class="pc-badge">' + c.badge + '</div>' : '') +
          '<div class="pc-name">' + c.name + '</div>' + c.price +
          '<div class="pc-tag">' + c.tagline + '</div>' +
          '<ul>' + c.features.map(function(f){ return '<li><span class="ck">\u2713</span>' + f + '</li>'; }).join('') + '</ul>' +
          '<a href="#pricing" data-cta="trial" class="btn ' + (c.highlighted ? 'btn-pri' : 'btn-ghost') + '" style="width:100%">' + c.cta + '</a>' +
          '</div>';
      }
      function renderPricing(data){
        var grid = document.getElementById('pricingGrid');
        if(!grid) return;
        var cards = [ cardHtml({ name:'Free Trial', price:'<div class="pc-price">' + trialLabel(data.trial_days) + ' free</div>', tagline:'Full access, no commitment', cta:'Start Free Trial', highlighted:false, badge:'', features:TRIAL_FEATURES }) ];
        (data.plans || []).forEach(function(p){
          cards.push(cardHtml({ name: esc(p.name), price: priceBlock(p), tagline: esc(p.tagline), cta: esc(p.cta_label || 'Start Free Trial'),
            highlighted: !!p.highlighted, badge: esc(p.badge_text || (p.highlighted ? 'Recommended' : '')), features: (p.features || []).map(esc) }));
        });
        grid.innerHTML = cards.join('');
      }
      function applyTrialPhrase(days){
        var next = Number(days) === 30 ? 'one month' : trialLabel(days);
        if (next === trialPhrase) return;
        trialPhrase = next;
        renderFaq();
      }
      (function loadPricing(){
        var grid = document.getElementById('pricingGrid');
        if (grid) grid.innerHTML = '<div class="pc-loading">Loading pricing\u2026</div>';
        var timer;
        var timeout = new Promise(function(resolve, reject){ timer = setTimeout(function(){ reject(new Error('timeout')); }, 6000); });
        Promise.race([Promise.resolve(supabase.rpc('get_landing_pricing')), timeout]).then(function(res){
          clearTimeout(timer);
          if (cancelled) return;
          var d = res && res.data;
          var data = (d && !res.error && Array.isArray(d.plans)) ? d : FALLBACK_PRICING;
          renderPricing(data);
          applyTrialPhrase(data.trial_days);
        }).catch(function(){
          clearTimeout(timer);
          if (cancelled) return;
          renderPricing(FALLBACK_PRICING);
        });
      })();
    
      // FAQ content + accordion
      var trialPhrase = 'one month';
      function buildFaqs(){ return [
        { q:'What is CostSpace?', a:'CostSpace is a construction cost management platform that brings budget, procurement, subcontracts, forecasting and reporting into a single system.' },
        { q:'Who is CostSpace for?', a:'Cost control engineers, quantity surveyors, project controls and commercial teams, estimators, and project managers on construction projects.' },
        { q:'What does the free trial include?', a:'Full access to the platform for ' + trialPhrase + '.' },
        { q:'How does pricing work?', a:'CostSpace uses a simple monthly subscription after the trial period ends. Current pricing is shown in the Pricing section above.' },
        { q:'Can I change my plan later?', a:'Yes — plans are designed to be adjusted as your needs change.' },
        { q:'Is project data isolated and access-controlled?', a:'Yes. Project data is isolated per organization, with role-based permissions controlling access throughout the platform.' },
        { q:'Does CostSpace support estimation as well as cost control?', a:'Yes. CostSpace includes a dedicated Estimation module covering takeoff, BOQ, rate build-up and tender pricing, alongside the cost-control workflow.' }
      ]; }
      function renderFaq(){
        var FAQS = buildFaqs();
        var list = document.getElementById('faqList');
        if(!list) return;
        list.innerHTML = FAQS.map(function(item, i){
          return '<div class="faq-item" data-i="' + i + '"><button type="button"><span>' + item.q + '</span><span class="fq-ic">+</span></button><div class="fq-a">' + item.a + '</div></div>';
        }).join('');
        list.querySelectorAll('.faq-item').forEach(function(el){
          el.querySelector('button').addEventListener('click', function(){
            var open = el.classList.contains('open');
            list.querySelectorAll('.faq-item').forEach(function(o){ o.classList.remove('open'); o.querySelector('.fq-ic').textContent='+'; });
            if(!open){ el.classList.add('open'); el.querySelector('.fq-ic').textContent='\u2212'; }
          });
        });
      }
      renderFaq();
    
    
    
      // nav contact popover
      var navContactCleanup = (function(){
        var btn = document.getElementById('navContactBtn');
        var pop = document.getElementById('navContactPop');
        if(!btn || !pop) return function(){};
        var toggleHandler = function(e){ e.stopPropagation(); pop.classList.toggle('open'); };
        var outsideHandler = function(e){ if(!pop.contains(e.target) && e.target!==btn) pop.classList.remove('open'); };
        btn.addEventListener('click', toggleHandler);
        document.addEventListener('click', outsideHandler);
        return function(){
          btn.removeEventListener('click', toggleHandler);
          document.removeEventListener('click', outsideHandler);
        };
      })();
    
      // nav login button -- present regardless of onLogin so the button
      // never disappears if this component is ever rendered without the
      // prop; it simply does nothing on click in that case.
      var navLoginCleanup = (function(){
        var btn = document.getElementById('navLoginBtn');
        if(!btn) return function(){};
        var handler = function(){ if (typeof onLogin === 'function') onLogin(); };
        btn.addEventListener('click', handler);
        return function(){ btn.removeEventListener('click', handler); };
      })();

      // tab switching for screenshots
      var shotTabHandlers = [];
      document.querySelectorAll('.shot-tab').forEach(function(tab){
        var handler = function(){
          var key = tab.getAttribute('data-shot');
          document.querySelectorAll('.shot-tab').forEach(function(t){t.classList.toggle('on', t===tab)});
          document.querySelectorAll('.shot').forEach(function(s){s.classList.toggle('on', s.getAttribute('data-shot')===key)});
        };
        tab.addEventListener('click', handler);
        shotTabHandlers.push([tab, handler]);
      });
    
      // scroll reveal
      var io = new IntersectionObserver(function(entries){
        entries.forEach(function(en){ if(en.isIntersecting){ en.target.classList.add('in'); io.unobserve(en.target); } });
      }, {threshold:.12});
      document.querySelectorAll('.rv').forEach(function(el){ io.observe(el); });

      // Integration: every "Start Free Trial" CTA in this HTML-injected
      // markup is a plain <a href="#pricing"> anchor (both the two
      // static ones and the pricing-card ones generated by
      // renderPricing() above) -- none of them originally pointed at
      // any login/auth entry point (this app is Invite-Only -- there is
      // no public signup route to link to). Intercepted here (same
      // pattern as the existing shot-tab/nav-contact-popover handlers
      // already in this effect) rather than editing the HTML strings
      // themselves, so the exact visual markup/copy is preserved
      // untouched. Falls through to the normal #pricing anchor behavior
      // if no onStartTrial was supplied (e.g. a bare preview render).
      var ctaHandler = function(e){
        var target = e.target.closest && e.target.closest('a.btn-pri, a.btn-ghost');
        if (!target) return;
        if (target.getAttribute('data-cta') !== 'trial' && target.textContent.trim().indexOf('Start Free Trial') !== 0) return;
        if (typeof onStartTrial !== 'function') return;
        e.preventDefault();
        onStartTrial();
      };
      if (rootRef.current) rootRef.current.addEventListener('click', ctaHandler);

    return () => {
      cancelled = true;
      styleEl.remove();
      navContactCleanup();
      navLoginCleanup();
      shotTabHandlers.forEach(function(pair){ pair[0].removeEventListener('click', pair[1]); });
      if (rootRef.current) rootRef.current.removeEventListener('click', ctaHandler);
    };
  }, [onStartTrial, onLogin]);

  return (
    <div ref={rootRef} dangerouslySetInnerHTML={{ __html: `

<!-- ============ NAV ============ -->
<header class="nav">
  <div class="wrap nav-in">
    <a class="logo" href="#top">
      <svg width="32" height="32" viewBox="0 0 100 100" aria-label="CostSpace">
        <rect width="100" height="100" rx="24" fill="#0B3D91"/>
        <path d="M50 18 A32 32 0 1 0 82 50 L68 50 A18 18 0 1 1 50 32 Z" fill="#FFFFFF"/>
        <circle cx="73" cy="50" r="7.5" fill="#5DADE2"/>
      </svg>
      <span class="wm">Cost<span class="b2">Space</span></span>
    </a>
    <nav class="nav-links">
      <a href="#problem">Why CostSpace</a>
      <a href="#features">Features</a>
      <a href="#screens">Product</a>
      <a href="#pricing">Pricing</a>
      <a href="#catalogue">Catalogue</a>
      <a href="#founder">Story</a>
    </nav>
    <div class="nav-cta">
      <div class="nav-contact" id="navContact">
        <button type="button" class="btn btn-ghost ghost-hide" id="navContactBtn">Contact</button>
        <div class="nc-pop" id="navContactPop">
          <img class="nc-qr" src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https%3A%2F%2Fwww.linkedin.com%2Fcompany%2Fcost-space%2F" alt="Scan to open CostSpace LinkedIn page" width="96" height="96" />
          <div class="nc-cap">Scan for LinkedIn</div>
          <div class="nc-links">
            <a href="mailto:wessam.taha.ahmed@gmail.com">wessam.taha.ahmed@gmail.com</a>
            <a href="tel:+201273556755">+20 127 355 6755</a>
            <a href="tel:+966532742763">+966 53 274 2763</a>
            <a href="https://wa.me/201273556755" target="_blank" rel="noopener">WhatsApp</a>
            <a href="https://www.linkedin.com/company/cost-space/" target="_blank" rel="noopener">LinkedIn</a>
          </div>
        </div>
      </div>
      <button type="button" class="btn btn-ghost" id="navLoginBtn">Login</button>
      <a href="#pricing" class="btn btn-pri">Start Free Trial</a>
    </div>
  </div>
</header>

<!-- ============ HERO ============ -->
<section class="hero" id="top">
  <div class="wrap hero-in">
    <div class="hero-copy">
      <span class="pill">Built for construction project controls</span>
      <h1>The Intelligence Layer Behind Project Performance.</h1>
      <p class="lede">CostSpace connects budgets, contracts, procurement, cash flow and forecasting into one system for construction projects.</p>
      <div class="hero-cta">
        <a href="#pricing" class="btn btn-pri">Start Free Trial
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
        </a>
        <a href="#features" class="btn btn-ghost">Explore CostSpace</a>
      </div>
      <div class="hero-trust">
        <div class="t"><span class="dot"></span> Purpose-built for construction</div>
      </div>
    </div>

    <div class="dash-frame rv">
      <!-- floating cards -->
      <div class="float-card fc-a">
        <div class="fi" style="background:rgba(43,182,115,.12);color:var(--green)">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17l6-6 4 4 8-8"/><path d="M21 7v5h-5"/></svg>
        </div>
        <div><div class="ft">+4.2% margin</div><div class="fs">Forecast at completion</div></div>
      </div>
      <div class="float-card fc-b">
        <div class="fi" style="background:var(--accent);color:var(--blue)">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
        </div>
        <div><div class="ft">3 POs approved</div><div class="fs">Procurement · today</div></div>
      </div>

      <div class="dash">
        <div class="topbar">
          <div class="dots"><span></span><span></span><span></span></div>
          <div class="crumb">costspace.app / executive-dashboard</div>
          <div class="live"><span class="d"></span> Live</div>
        </div>
        <div class="body">
          <div class="side">
            <div class="sb-h">Project Controls</div>
            <div class="it on"><span class="ic"></span> Dashboard</div>
            <div class="it"><span class="ic"></span> Budget</div>
            <div class="it"><span class="ic"></span> Contracts</div>
            <div class="it"><span class="ic"></span> Procurement</div>
            <div class="it"><span class="ic"></span> Cash Flow</div>
            <div class="it"><span class="ic"></span> Forecast</div>
            <div class="sb-h">Reports</div>
            <div class="it"><span class="ic"></span> Cost Report</div>
          </div>
          <div class="main">
            <div class="kpis">
              <div class="kpi"><div class="k">Budget</div><div class="v">SAR 482M</div><div class="c up">▲ on plan</div></div>
              <div class="kpi"><div class="k">Committed</div><div class="v">SAR 391M</div><div class="c up">81% allocated</div></div>
              <div class="kpi"><div class="k">Variance</div><div class="v">−SAR 6.4M</div><div class="c dn">▼ 1.3% risk</div></div>
            </div>
            <div class="charts">
              <div class="card">
                <div class="ch">Planned vs Actual Cost</div>
                <div class="cs">SAR (millions) · last 8 periods</div>
                <div class="bars">
                  <div class="b"><div class="stack planned" style="height:34%"></div><div class="stack actual" style="height:30%"></div><div class="lbl">P1</div></div>
                  <div class="b"><div class="stack planned" style="height:46%"></div><div class="stack actual" style="height:42%"></div><div class="lbl">P2</div></div>
                  <div class="b"><div class="stack planned" style="height:55%"></div><div class="stack actual" style="height:58%"></div><div class="lbl">P3</div></div>
                  <div class="b"><div class="stack planned" style="height:62%"></div><div class="stack actual" style="height:60%"></div><div class="lbl">P4</div></div>
                  <div class="b"><div class="stack planned" style="height:70%"></div><div class="stack actual" style="height:74%"></div><div class="lbl">P5</div></div>
                  <div class="b"><div class="stack planned" style="height:80%"></div><div class="stack actual" style="height:77%"></div><div class="lbl">P6</div></div>
                  <div class="b"><div class="stack planned" style="height:90%"></div><div class="stack actual" style="height:94%"></div><div class="lbl">P7</div></div>
                  <div class="b"><div class="stack planned" style="height:100%"></div><div class="stack actual" style="height:96%"></div><div class="lbl">P8</div></div>
                </div>
              </div>
              <div class="card">
                <div class="ch">Cost Breakdown</div>
                <div class="cs">By category</div>
                <div class="donut-wrap">
                  <div class="donut"></div>
                  <div class="leg">
                    <div class="l"><span class="sw" style="background:var(--blue)"></span> Subcontracts</div>
                    <div class="l"><span class="sw" style="background:var(--blue-2)"></span> Materials</div>
                    <div class="l"><span class="sw" style="background:#D4E3F5"></span> Indirect</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ============ TRUST STRIP ============ -->
<div class="strip">
  <div class="wrap strip-in">
    <span class="lab">Built around the realities of</span>
    <span class="item">Contractors</span>
    <span class="item">PMCs</span>
    <span class="item">Developers</span>
    <span class="item">Cost Consultants</span>
    <span class="item">Project Controls</span>
  </div>
</div>

<!-- ============ PROBLEM ============ -->
<section class="sec prob" id="problem">
  <div class="wrap">
    <div class="sechead rv">
      <span class="ey lt">The problem</span>
      <h2>Cost control still lives in spreadsheets.</h2>
      <p>On most projects, financial truth is scattered across files, inboxes and people — and by the time numbers are reconciled, the decision window has already closed.</p>
    </div>
    <div class="sol-in" style="grid-template-columns:1fr 1.1fr">
      <div class="num-list">
        <div class="num-item rv"><span class="nn">01</span><div><h4>Excel-based tracking</h4><p>Budgets and commitments managed in fragile, versioned spreadsheets that break the moment a project scales.</p></div></div>
        <div class="num-item rv"><span class="nn">02</span><div><h4>Fragmented data</h4><p>Budget, contracts and procurement sit in separate tools that never agree on a single number.</p></div></div>
        <div class="num-item rv"><span class="nn">03</span><div><h4>Delayed reporting</h4><p>Monthly cost reports arrive weeks late — describing a problem that has already happened.</p></div></div>
        <div class="num-item rv"><span class="nn">04</span><div><h4>No real-time visibility</h4><p>Leadership can't see where a project actually stands until the period closes.</p></div></div>
      </div>
      <div class="cmp-wrap rv">
        <div class="cmp-row h"><div>Same project, two ways to run it</div><div>Spreadsheets</div><div class="hl">CostSpace</div></div>
        <div class="cmp-row"><div class="lbl">Single source of truth</div><div class="no">✕</div><div class="hl-cell yes">✓</div></div>
        <div class="cmp-row"><div class="lbl">Real-time cost position</div><div class="no">✕</div><div class="hl-cell yes">✓</div></div>
        <div class="cmp-row"><div class="lbl">Connected procurement &amp; forecast</div><div class="no">✕</div><div class="hl-cell yes">✓</div></div>
        <div class="cmp-row"><div class="lbl">Built for construction workflows</div><div class="no">✕</div><div class="hl-cell yes">✓</div></div>
        <div class="cmp-row"><div class="lbl">Full audit trail on every change</div><div class="no">✕</div><div class="hl-cell yes">✓</div></div>
      </div>
    </div>
    <div class="prob-quote rv">
      <div class="q">Project teams spend more time <span class="hl">collecting data</span> than <span class="hl">controlling costs</span>.</div>
    </div>
  </div>
</section>

<!-- ============ SOLUTION ============ -->
<section class="sec" id="solution">
  <div class="wrap">
    <div class="sechead left rv">
      <span class="ey">The CostSpace way</span>
      <h2>One system. One number everyone trusts.</h2>
      <p>CostSpace replaces the patchwork of spreadsheets and disconnected tools with a single, connected cost control platform — purpose-built for construction.</p>
    </div>
    <div class="sol-in">
      <div class="sol-list">
        <div class="sol-item rv">
          <div class="si"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l9 5v10l-9 5-9-5V7z"/><path d="M12 22V12M3 7l9 5 9-5"/></svg></div>
          <div><h3>Single source of truth</h3><p>Every budget line, commitment and actual lives in one connected model — no more reconciling versions.</p></div>
        </div>
        <div class="sol-item rv">
          <div class="si"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><path d="M7 14l4-4 3 3 5-6"/></svg></div>
          <div><h3>Real-time dashboards</h3><p>Cost position, variance and exposure update the moment data changes — not at month-end.</p></div>
        </div>
        <div class="sol-item rv">
          <div class="si"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="6" r="3"/><circle cx="18" cy="18" r="3"/><path d="M9 6h6a3 3 0 0 1 3 3v6"/></svg></div>
          <div><h3>Connected financial modules</h3><p>Budgets flow into contracts, procurement, cash flow and forecasting — each step builds on the last.</p></div>
        </div>
        <div class="sol-item rv">
          <div class="si"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z"/></svg></div>
          <div><h3>Better decisions, sooner</h3><p>See the impact of a variation or delay before you commit — not after it hits the bottom line.</p></div>
        </div>
      </div>
      <div class="hub rv">
        <div class="hub-ring">
          <div class="hub-node n1"><span class="d"></span> Budget</div>
          <div class="hub-node n2"><span class="d"></span> Contracts</div>
          <div class="hub-node n3"><span class="d"></span> Procurement</div>
          <div class="hub-node n4"><span class="d"></span> Cash Flow</div>
          <div class="hub-node n5"><span class="d"></span> Forecast</div>
          <div class="hub-node n6"><span class="d"></span> Reports</div>
        </div>
        <div class="hub-core">
          <span class="wm">Cost<span class="b2">Space</span></span>
          <span class="sub">Cost Engine</span>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ============ COST CONTROL LIFECYCLE ============ -->
<section class="sec" id="lifecycle" style="background:var(--bg)">
  <div class="wrap">
    <div class="sechead rv">
      <span class="ey">Cost control</span>
      <h2>One connected cost position, start to finish.</h2>
      <p>Budget, commitment, actual cost and forecast move through the same model — so every number reconciles automatically, all the way to the final report.</p>
    </div>
    <div class="flow-wrap rv">
      <div class="flow-step"><span class="fn">01</span><h4>Budget</h4></div>
      <span class="flow-arrow"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
      <div class="flow-step"><span class="fn">02</span><h4>Commitment</h4></div>
      <span class="flow-arrow"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
      <div class="flow-step"><span class="fn">03</span><h4>Actual Cost</h4></div>
      <span class="flow-arrow"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
      <div class="flow-step"><span class="fn">04</span><h4>Forecast</h4></div>
      <span class="flow-arrow"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
      <div class="flow-step"><span class="fn">05</span><h4>EAC</h4></div>
      <span class="flow-arrow"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
      <div class="flow-step"><span class="fn">06</span><h4>Variance</h4></div>
      <span class="flow-arrow"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
      <div class="flow-step"><span class="fn">07</span><h4>Reporting</h4></div>
    </div>
    <div class="flow-card rv">
      <div class="fc-i"><div class="fc-k">Budget</div><div class="fc-v">Baseline</div><div class="fc-s">Original + revised, by cost code</div></div>
      <div class="fc-i"><div class="fc-k">Committed</div><div class="fc-v">Locked-in</div><div class="fc-s">Contracts &amp; purchase orders</div></div>
      <div class="fc-i"><div class="fc-k">Forecast / EAC</div><div class="fc-v">Projected</div><div class="fc-s">Cost-to-complete, updated live</div></div>
      <div class="fc-i"><div class="fc-k">Variance</div><div class="fc-v">Explained</div><div class="fc-s">Against budget, with a clear reason</div></div>
    </div>
  </div>
</section>

<!-- ============ FEATURES ============ -->
<section class="sec" id="features" style="background:var(--bg)">
  <div class="wrap">
    <div class="sechead rv">
      <span class="ey">Capabilities</span>
      <h2>Everything you need to control project cost.</h2>
      <p>Six connected modules that cover the full commercial lifecycle of a construction project.</p>
    </div>
    <div class="feat-rows">
      <div class="feat-row rv">
        <div class="fi"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><rect x="7" y="11" width="3" height="7"/><rect x="12" y="7" width="3" height="11"/><rect x="17" y="13" width="3" height="5"/></svg></div>
        <div><h3>Budget Control</h3><p>Structured cost breakdown with live tracking of original, revised, committed and forecast budgets.</p></div>
      </div>
      <div class="feat-row rv">
        <div class="fi"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h4"/></svg></div>
        <div><h3>Contract Management</h3><p>Manage contracts, variations and certificates with a full audit trail of every commercial change.</p></div>
      </div>
      <div class="feat-row rv">
        <div class="fi"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="20" r="1.5"/><circle cx="18" cy="20" r="1.5"/><path d="M2 3h3l2.5 13h11l2-9H6"/></svg></div>
        <div><h3>Procurement Tracking</h3><p>Follow every PR, RFQ and PO from request to award with full status visibility.</p></div>
      </div>
      <div class="feat-row rv">
        <div class="fi"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18v12H3z"/><path d="M3 10h18M7 15h4"/></svg></div>
        <div><h3>Cash Flow Monitoring</h3><p>Project inflows and outflows period by period to stay ahead of funding gaps.</p></div>
      </div>
      <div class="feat-row rv">
        <div class="fi"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17l6-6 4 4 8-8"/><path d="M21 7v5h-5"/></svg></div>
        <div><h3>Forecasting Engine</h3><p>Cost-to-complete and forecast-at-completion that update automatically as the project moves.</p></div>
      </div>
      <div class="feat-row rv">
        <div class="fi"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/></svg></div>
        <div><h3>Executive Dashboard</h3><p>One board-ready view of cost, risk and exposure across every project in the portfolio.</p></div>
      </div>
    </div>
  </div>
</section>

<!-- ============ SCREENSHOTS ============ -->
<section class="sec shots" id="screens">
  <div class="wrap">
    <div class="sechead rv">
      <span class="ey">Inside the product</span>
      <h2>Designed for the way cost teams actually work.</h2>
      <p>Clean, dense and fast — built to replace the spreadsheet, not add another tool.</p>
    </div>
    <div class="shot-tabs rv">
      <button class="shot-tab on" data-shot="dash">Executive Dashboard</button>
      <button class="shot-tab" data-shot="budget">Budget Module</button>
      <button class="shot-tab" data-shot="proc">Contracts &amp; Procurement</button>
      <button class="shot-tab" data-shot="forecast">Forecasting &amp; EAC</button>
      <button class="shot-tab" data-shot="estimation">Estimation</button>
    </div>
    <div class="shot-stage rv">
      <!-- DASH -->
      <div class="shot on" data-shot="dash">
        <div class="app">
          <div class="aside">
            <div class="brand"><svg width="22" height="22" viewBox="0 0 100 100"><rect width="100" height="100" rx="24" fill="#0B3D91"/><path d="M50 18 A32 32 0 1 0 82 50 L68 50 A18 18 0 1 1 50 32 Z" fill="#FFFFFF"/><circle cx="73" cy="50" r="7.5" fill="#5DADE2"/></svg><span class="wm">Cost<span class="b2">Space</span></span></div>
            <div class="it on"><span class="ic"></span> Dashboard</div>
            <div class="it"><span class="ic"></span> Budget</div>
            <div class="it"><span class="ic"></span> Contracts</div>
            <div class="it"><span class="ic"></span> Procurement</div>
            <div class="it"><span class="ic"></span> Cash Flow</div>
            <div class="it"><span class="ic"></span> Forecast</div>
            <div class="it"><span class="ic"></span> Reports</div>
          </div>
          <div class="view">
            <div class="vbar"><span class="vt">Executive Dashboard</span><span class="vtag">All Projects</span><span class="av">AM</span></div>
            <div class="vbody">
              <div class="kpis" style="display:grid;grid-template-columns:repeat(4,1fr);gap:14px">
                <div class="kpi" style="background:#fff;border:1px solid var(--line);border-radius:12px;padding:16px"><div class="k" style="font-family:var(--mono);font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--slate)">Total Budget</div><div class="v" style="font-family:var(--disp);font-size:23px;font-weight:600;color:var(--ink);margin-top:8px">SAR 1.84B</div><div style="font-size:11px;color:var(--green);margin-top:5px;font-weight:600">5 active projects</div></div>
                <div class="kpi" style="background:#fff;border:1px solid var(--line);border-radius:12px;padding:16px"><div class="k" style="font-family:var(--mono);font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--slate)">Committed</div><div class="v" style="font-family:var(--disp);font-size:23px;font-weight:600;color:var(--ink);margin-top:8px">SAR 1.41B</div><div style="font-size:11px;color:var(--slate);margin-top:5px;font-weight:600">77% of budget</div></div>
                <div class="kpi" style="background:#fff;border:1px solid var(--line);border-radius:12px;padding:16px"><div class="k" style="font-family:var(--mono);font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--slate)">Forecast Variance</div><div class="v" style="font-family:var(--disp);font-size:23px;font-weight:600;color:var(--red);margin-top:8px">−SAR 22M</div><div style="font-size:11px;color:var(--red);margin-top:5px;font-weight:600">▼ 1.2% over</div></div>
                <div class="kpi" style="background:var(--blue);border-radius:12px;padding:16px"><div class="k" style="font-family:var(--mono);font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:rgba(255,255,255,.65)">Cash Position</div><div class="v" style="font-family:var(--disp);font-size:23px;font-weight:600;color:#fff;margin-top:8px">SAR 96M</div><div style="font-size:11px;color:var(--blue-2);margin-top:5px;font-weight:600">▲ healthy</div></div>
              </div>
              <div style="display:grid;grid-template-columns:1.6fr 1fr;gap:14px;flex:1">
                <div class="card" style="background:#fff;border:1px solid var(--line);border-radius:12px;padding:18px;display:flex;flex-direction:column">
                  <div class="ch" style="font-family:var(--disp);font-size:13px;font-weight:600;margin-bottom:2px">Portfolio Cost Performance</div>
                  <div class="cs" style="font-family:var(--mono);font-size:10px;color:var(--slate);margin-bottom:16px">Planned vs Actual · SAR (millions)</div>
                  <div class="bars" style="min-height:140px">
                    <div class="b"><div class="stack planned" style="height:40%"></div><div class="stack actual" style="height:37%"></div><div class="lbl">JAN</div></div>
                    <div class="b"><div class="stack planned" style="height:52%"></div><div class="stack actual" style="height:49%"></div><div class="lbl">FEB</div></div>
                    <div class="b"><div class="stack planned" style="height:58%"></div><div class="stack actual" style="height:62%"></div><div class="lbl">MAR</div></div>
                    <div class="b"><div class="stack planned" style="height:66%"></div><div class="stack actual" style="height:63%"></div><div class="lbl">APR</div></div>
                    <div class="b"><div class="stack planned" style="height:74%"></div><div class="stack actual" style="height:79%"></div><div class="lbl">MAY</div></div>
                    <div class="b"><div class="stack planned" style="height:84%"></div><div class="stack actual" style="height:81%"></div><div class="lbl">JUN</div></div>
                    <div class="b"><div class="stack planned" style="height:100%"></div><div class="stack actual" style="height:97%"></div><div class="lbl">JUL</div></div>
                  </div>
                </div>
                <div class="card" style="background:#fff;border:1px solid var(--line);border-radius:12px;padding:18px;display:flex;flex-direction:column">
                  <div class="ch" style="font-family:var(--disp);font-size:13px;font-weight:600;margin-bottom:2px">Spend by Category</div>
                  <div class="cs" style="font-family:var(--mono);font-size:10px;color:var(--slate);margin-bottom:16px">Current period</div>
                  <div class="donut-wrap">
                    <div class="donut" style="width:118px;height:118px"></div>
                    <div class="leg">
                      <div class="l"><span class="sw" style="background:var(--blue)"></span> Subcontracts · 62%</div>
                      <div class="l"><span class="sw" style="background:var(--blue-2)"></span> Materials · 20%</div>
                      <div class="l"><span class="sw" style="background:#D4E3F5"></span> Indirect · 18%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- BUDGET -->
      <div class="shot" data-shot="budget">
        <div class="app">
          <div class="aside">
            <div class="brand"><svg width="22" height="22" viewBox="0 0 100 100"><rect width="100" height="100" rx="24" fill="#0B3D91"/><path d="M50 18 A32 32 0 1 0 82 50 L68 50 A18 18 0 1 1 50 32 Z" fill="#FFFFFF"/><circle cx="73" cy="50" r="7.5" fill="#5DADE2"/></svg><span class="wm">Cost<span class="b2">Space</span></span></div>
            <div class="it"><span class="ic"></span> Dashboard</div>
            <div class="it on"><span class="ic"></span> Budget</div>
            <div class="it"><span class="ic"></span> Contracts</div>
            <div class="it"><span class="ic"></span> Procurement</div>
            <div class="it"><span class="ic"></span> Cash Flow</div>
            <div class="it"><span class="ic"></span> Forecast</div>
            <div class="it"><span class="ic"></span> Reports</div>
          </div>
          <div class="view">
            <div class="vbar"><span class="vt">Budget Control</span><span class="vtag">NEOM Waterfront Tower</span><span class="av">AM</span></div>
            <div class="vbody">
              <div class="tbl">
                <div class="tr h"><div>Cost Code</div><div>Budget</div><div>Committed</div><div>Forecast</div><div>Status</div></div>
                <div class="tr"><div class="nm">01 · Substructure</div><div class="mono">82.0M</div><div class="mono">79.4M</div><div class="mono">80.1M</div><div><span class="tag-st st-ok">On budget</span></div></div>
                <div class="tr"><div class="nm">02 · Superstructure</div><div class="mono">146.0M</div><div class="mono">138.2M</div><div class="mono">149.7M</div><div><span class="tag-st st-warn">Watch</span></div></div>
                <div class="tr"><div class="nm">03 · Façade</div><div class="mono">68.5M</div><div class="mono">71.9M</div><div class="mono">74.2M</div><div><span class="tag-st st-risk">Over</span></div></div>
                <div class="tr"><div class="nm">04 · MEP</div><div class="mono">112.0M</div><div class="mono">96.3M</div><div class="mono">109.5M</div><div><span class="tag-st st-ok">On budget</span></div></div>
                <div class="tr"><div class="nm">05 · Finishes</div><div class="mono">54.0M</div><div class="mono">38.1M</div><div class="mono">52.8M</div><div><span class="tag-st st-ok">On budget</span></div></div>
                <div class="tr"><div class="nm">06 · External Works</div><div class="mono">19.5M</div><div class="mono">12.4M</div><div class="mono">19.0M</div><div><span class="tag-st st-ok">On budget</span></div></div>
              </div>
              <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:14px">
                <div class="card" style="background:#fff;border:1px solid var(--line);border-radius:12px;padding:16px"><div style="font-family:var(--mono);font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--slate)">Original Budget</div><div style="font-family:var(--disp);font-size:21px;font-weight:600;margin-top:8px">SAR 482M</div></div>
                <div class="card" style="background:#fff;border:1px solid var(--line);border-radius:12px;padding:16px"><div style="font-family:var(--mono);font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--slate)">Committed</div><div style="font-family:var(--disp);font-size:21px;font-weight:600;margin-top:8px">SAR 436M</div><div class="prog" style="margin-top:10px"><i style="width:81%"></i></div></div>
                <div class="card" style="background:#fff;border:1px solid var(--line);border-radius:12px;padding:16px"><div style="font-family:var(--mono);font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--slate)">Forecast Final</div><div style="font-family:var(--disp);font-size:21px;font-weight:600;margin-top:8px;color:var(--red)">SAR 488M</div><div style="font-size:11px;color:var(--red);margin-top:5px;font-weight:600">+SAR 6M over</div></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- PROC -->
      <div class="shot" data-shot="proc">
        <div class="app">
          <div class="aside">
            <div class="brand"><svg width="22" height="22" viewBox="0 0 100 100"><rect width="100" height="100" rx="24" fill="#0B3D91"/><path d="M50 18 A32 32 0 1 0 82 50 L68 50 A18 18 0 1 1 50 32 Z" fill="#FFFFFF"/><circle cx="73" cy="50" r="7.5" fill="#5DADE2"/></svg><span class="wm">Cost<span class="b2">Space</span></span></div>
            <div class="it"><span class="ic"></span> Dashboard</div>
            <div class="it"><span class="ic"></span> Budget</div>
            <div class="it on"><span class="ic"></span> Procurement</div>
            <div class="it"><span class="ic"></span> Contracts</div>
            <div class="it"><span class="ic"></span> Cash Flow</div>
            <div class="it"><span class="ic"></span> Forecast</div>
            <div class="it"><span class="ic"></span> Reports</div>
          </div>
          <div class="view">
            <div class="vbar"><span class="vt">Procurement Tracking</span><span class="vtag">PR · RFQ · PO</span><span class="av">AM</span></div>
            <div class="vbody">
              <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:14px">
                <div class="card" style="background:#fff;border:1px solid var(--line);border-radius:12px;padding:14px"><div style="font-family:var(--mono);font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--slate)">Open PRs</div><div style="font-family:var(--disp);font-size:20px;font-weight:600;margin-top:7px">14</div></div>
                <div class="card" style="background:#fff;border:1px solid var(--line);border-radius:12px;padding:14px"><div style="font-family:var(--mono);font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--slate)">RFQs Out</div><div style="font-family:var(--disp);font-size:20px;font-weight:600;margin-top:7px">8</div></div>
                <div class="card" style="background:#fff;border:1px solid var(--line);border-radius:12px;padding:14px"><div style="font-family:var(--mono);font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--slate)">POs Awaiting</div><div style="font-family:var(--disp);font-size:20px;font-weight:600;margin-top:7px;color:var(--amber)">5</div></div>
                <div class="card" style="background:#fff;border:1px solid var(--line);border-radius:12px;padding:14px"><div style="font-family:var(--mono);font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--slate)">Committed (mo)</div><div style="font-family:var(--disp);font-size:20px;font-weight:600;margin-top:7px">SAR 34M</div></div>
              </div>
              <div class="tbl">
                <div class="tr h"><div>Reference</div><div>Package</div><div>Vendor</div><div>Value</div><div>Status</div></div>
                <div class="tr"><div class="mono">PO-2041</div><div class="nm" style="font-weight:500">Curtain Wall</div><div>AlumTech Co.</div><div class="mono">12.8M</div><div><span class="tag-st st-ok">Awarded</span></div></div>
                <div class="tr"><div class="mono">RFQ-1188</div><div class="nm" style="font-weight:500">MEP Ductwork</div><div>4 bidders</div><div class="mono">—</div><div><span class="tag-st st-warn">In bid</span></div></div>
                <div class="tr"><div class="mono">PO-2039</div><div class="nm" style="font-weight:500">Rebar Supply</div><div>Saudi Steel</div><div class="mono">9.4M</div><div><span class="tag-st st-ok">Awarded</span></div></div>
                <div class="tr"><div class="mono">PR-0571</div><div class="nm" style="font-weight:500">Lifts &amp; Escalators</div><div>Pending RFQ</div><div class="mono">—</div><div><span class="tag-st st-risk">Approval</span></div></div>
                <div class="tr"><div class="mono">PO-2036</div><div class="nm" style="font-weight:500">Ready-mix Concrete</div><div>BuildMix</div><div class="mono">6.1M</div><div><span class="tag-st st-ok">Awarded</span></div></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- FORECAST -->
      <div class="shot" data-shot="forecast">
        <div class="app">
          <div class="aside">
            <div class="brand"><svg width="22" height="22" viewBox="0 0 100 100"><rect width="100" height="100" rx="24" fill="#0B3D91"/><path d="M50 18 A32 32 0 1 0 82 50 L68 50 A18 18 0 1 1 50 32 Z" fill="#FFFFFF"/><circle cx="73" cy="50" r="7.5" fill="#5DADE2"/></svg><span class="wm">Cost<span class="b2">Space</span></span></div>
            <div class="it"><span class="ic"></span> Dashboard</div>
            <div class="it"><span class="ic"></span> Budget</div>
            <div class="it"><span class="ic"></span> Procurement</div>
            <div class="it on"><span class="ic"></span> Forecast</div>
            <div class="it"><span class="ic"></span> Cash Flow</div>
            <div class="it"><span class="ic"></span> Reports</div>
          </div>
          <div class="view">
            <div class="vbar"><span class="vt">Forecast &amp; EAC</span><span class="vtag">Cost-to-complete</span><span class="av">AM</span></div>
            <div class="vbody">
              <div class="tbl">
                <div class="tr h"><div>Cost Code</div><div>Budget</div><div>Actual</div><div>Forecast</div><div>EAC Variance</div></div>
                <div class="tr"><div class="nm">01 · Substructure</div><div class="mono">82.0M</div><div class="mono">61.4M</div><div class="mono">80.1M</div><div><span class="tag-st st-ok">▲ 1.9M</span></div></div>
                <div class="tr"><div class="nm">02 · Superstructure</div><div class="mono">146.0M</div><div class="mono">98.2M</div><div class="mono">149.7M</div><div><span class="tag-st st-warn">▼ 3.7M</span></div></div>
                <div class="tr"><div class="nm">03 · Façade</div><div class="mono">68.5M</div><div class="mono">44.6M</div><div class="mono">74.2M</div><div><span class="tag-st st-risk">▼ 5.7M</span></div></div>
                <div class="tr"><div class="nm">04 · MEP</div><div class="mono">112.0M</div><div class="mono">70.1M</div><div class="mono">109.5M</div><div><span class="tag-st st-ok">▲ 2.5M</span></div></div>
              </div>
              <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:14px">
                <div class="card" style="background:#fff;border:1px solid var(--line);border-radius:12px;padding:16px"><div style="font-family:var(--mono);font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--slate)">Forecast at Completion</div><div style="font-family:var(--disp);font-size:21px;font-weight:600;margin-top:8px;color:var(--red)">SAR 488M</div></div>
                <div class="card" style="background:#fff;border:1px solid var(--line);border-radius:12px;padding:16px"><div style="font-family:var(--mono);font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--slate)">Cost to Complete</div><div style="font-family:var(--disp);font-size:21px;font-weight:600;margin-top:8px">SAR 254M</div></div>
                <div class="card" style="background:var(--blue);border-radius:12px;padding:16px"><div style="font-family:var(--mono);font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:rgba(255,255,255,.65)">Variance vs Budget</div><div style="font-family:var(--disp);font-size:21px;font-weight:600;margin-top:8px;color:#fff">−SAR 6M</div></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- ESTIMATION -->
      <div class="shot" data-shot="estimation">
        <div class="app">
          <div class="aside">
            <div class="brand"><svg width="22" height="22" viewBox="0 0 100 100"><rect width="100" height="100" rx="24" fill="#0B3D91"/><path d="M50 18 A32 32 0 1 0 82 50 L68 50 A18 18 0 1 1 50 32 Z" fill="#FFFFFF"/><circle cx="73" cy="50" r="7.5" fill="#5DADE2"/></svg><span class="wm">Cost<span class="b2">Space</span></span></div>
            <div class="it"><span class="ic"></span> Dashboard</div>
            <div class="it"><span class="ic"></span> Budget</div>
            <div class="it on"><span class="ic"></span> Estimation</div>
            <div class="it"><span class="ic"></span> Procurement</div>
            <div class="it"><span class="ic"></span> Forecast</div>
          </div>
          <div class="view">
            <div class="vbar"><span class="vt">Estimation</span><span class="vtag">Tender &amp; Rate Build-Up</span><span class="av">AM</span></div>
            <div class="vbody">
              <div class="flow-wrap" style="justify-content:flex-start;transform:scale(.86);transform-origin:left top;margin:0 0 -8px -6px">
                <div class="flow-step" style="min-width:104px;padding:14px 16px"><h4 style="font-size:13px">Takeoff</h4></div>
                <span class="flow-arrow"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
                <div class="flow-step" style="min-width:104px;padding:14px 16px"><h4 style="font-size:13px">BOQ</h4></div>
                <span class="flow-arrow"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
                <div class="flow-step" style="min-width:104px;padding:14px 16px"><h4 style="font-size:13px">Rate Build-Up</h4></div>
                <span class="flow-arrow"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
                <div class="flow-step" style="min-width:104px;padding:14px 16px"><h4 style="font-size:13px">Direct Budget</h4></div>
                <span class="flow-arrow"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
                <div class="flow-step" style="min-width:104px;padding:14px 16px"><h4 style="font-size:13px">Indirect Budget</h4></div>
                <span class="flow-arrow"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
                <div class="flow-step" style="min-width:104px;padding:14px 16px"><h4 style="font-size:13px">Markup</h4></div>
                <span class="flow-arrow"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
                <div class="flow-step" style="min-width:104px;padding:14px 16px;border-color:var(--blue);color:var(--blue)"><h4 style="font-size:13px;color:var(--blue)">Tender / Revision</h4></div>
              </div>
              <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-top:22px">
                <div class="card" style="background:#fff;border:1px solid var(--line);border-radius:12px;padding:14px"><div style="font-family:var(--mono);font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--slate)">Direct Cost</div><div style="font-family:var(--disp);font-size:19px;font-weight:600;margin-top:7px">SAR 402M</div></div>
                <div class="card" style="background:#fff;border:1px solid var(--line);border-radius:12px;padding:14px"><div style="font-family:var(--mono);font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--slate)">Indirect Cost</div><div style="font-family:var(--disp);font-size:19px;font-weight:600;margin-top:7px">SAR 58M</div></div>
                <div class="card" style="background:#fff;border:1px solid var(--line);border-radius:12px;padding:14px"><div style="font-family:var(--mono);font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--slate)">Markup</div><div style="font-family:var(--disp);font-size:19px;font-weight:600;margin-top:7px">6.5%</div></div>
                <div class="card" style="background:var(--blue);border-radius:12px;padding:14px"><div style="font-family:var(--mono);font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:rgba(255,255,255,.65)">Tender Value</div><div style="font-family:var(--disp);font-size:19px;font-weight:600;margin-top:7px;color:#fff">SAR 490M</div></div>
              </div>
              <div style="font-size:12.5px;color:var(--slate);margin-top:4px">An awarded estimate flows straight into Budget Control — no re-entry, no reconciliation.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ============ USERS ============ -->
<section class="sec" id="users">
  <div class="wrap">
    <div class="sechead rv">
      <span class="ey">Who it's for</span>
      <h2>One platform, every seat at the cost table.</h2>
      <p>From the boardroom to the cost engineer's desk — everyone works from the same numbers.</p>
    </div>
    <div class="persona-list">
      <div class="persona-item rv"><span class="pi-n">01</span><div><h3>Cost Control Engineers &amp; Quantity Surveyors</h3><p>Manage cost codes, commitments and forecasts against one live budget — without wrestling spreadsheets.</p></div></div>
      <div class="persona-item rv"><span class="pi-n">02</span><div><h3>Project Controls &amp; Commercial Teams</h3><p>Control contracts, variations and procurement exposure with a complete commercial audit trail.</p></div></div>
      <div class="persona-item rv"><span class="pi-n">03</span><div><h3>Estimators</h3><p>Move from takeoff to a priced tender, then hand a clean budget straight to the project team.</p></div></div>
      <div class="persona-item rv"><span class="pi-n">04</span><div><h3>Project Managers &amp; Directors</h3><p>Portfolio-wide visibility of cost, risk and cash across every project, in one place.</p></div></div>
    </div>
  </div>
</section>

<!-- ============ FOUNDER ============ -->
<section class="sec founder" id="founder">
  <div class="wrap">
    <div class="quote-big rv">
      <div class="q">"I lived the chaos of controlling costs in spreadsheets. CostSpace is the system I always wished I had on site."</div>
      <div class="attr">
        <div class="av2">CS</div>
        <div style="text-align:left"><div class="nm">Built by a Cost Control Engineer</div><div class="rl">Founder · CostSpace</div></div>
      </div>
    </div>
    <div class="founder-stats" style="justify-content:center;margin-top:44px">
      <div class="s"><div class="v">10+ yrs</div><div class="l">In project controls</div></div>
      <div class="s"><div class="v">6</div><div class="l">Connected modules</div></div>
      <div class="s"><div class="v">1</div><div class="l">Source of truth</div></div>
    </div>
  </div>
</section>

<!-- ============ HOW IT WORKS ============ -->
<section class="sec" id="how">
  <div class="wrap">
    <div class="sechead rv">
      <span class="ey">Getting started</span>
      <h2>From spreadsheet to system, in three steps.</h2>
    </div>
    <div class="steps3">
      <div class="step3 rv"><div class="s3n">1</div><h4>Start your free trial</h4><p>Create your workspace and add your first project in minutes.</p></div>
      <div class="step3 rv"><div class="s3n">2</div><h4>Set up your cost structure</h4><p>Bring in budgets, cost codes and contracts, or start fresh with a clean structure.</p></div>
      <div class="step3 rv"><div class="s3n">3</div><h4>Run cost control, live</h4><p>Track commitments, actuals and forecast — with one connected number everyone trusts.</p></div>
    </div>
  </div>
</section>

<!-- ============ PRICING ============ -->
<section class="sec" id="pricing" style="background:var(--bg)">
  <div class="wrap">
    <div class="sechead rv">
      <span class="ey">Pricing</span>
      <h2>Simple, transparent pricing.</h2>
      <p>Start free. Pay only when you're ready to continue.</p>
    </div>
    <div class="pricing-grid rv" id="pricingGrid"></div>
  </div>
</section>

<!-- ============ CATALOGUE ============ -->
<section class="sec" id="catalogue">
  <div class="wrap">
    <div class="cat-card rv">
      <div class="cat-lock"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg></div>
      <span class="ey">Product catalogue</span>
      <h2 style="font-size:28px;margin-top:12px">Explore the CostSpace Product Catalogue.</h2>
      <p style="font-size:15.5px;color:var(--slate);margin-top:14px;max-width:520px;margin-left:auto;margin-right:auto">Every module, workflow and report, explained in full — available inside the platform once you sign in. It isn't published publicly, so your project data and workflows stay yours.</p>
      <a href="#pricing" class="btn btn-pri" style="margin-top:26px">View Product Catalogue
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
      </a>
    </div>
  </div>
</section>

<!-- ============ FAQ ============ -->
<section class="sec" id="faq" style="background:var(--bg)">
  <div class="wrap">
    <div class="sechead rv">
      <span class="ey">FAQ</span>
      <h2>Frequently asked questions.</h2>
    </div>
    <div class="faq rv" id="faqList"></div>
  </div>
</section>

<!-- ============ CTA / FORM ============ -->
</section>

<!-- ============ FOOTER ============ -->
<footer class="ft">
  <div class="wrap ft-in">
    <div class="ft-brand">
      <svg width="30" height="30" viewBox="0 0 100 100"><rect width="100" height="100" rx="24" fill="#0B3D91"/><path d="M50 18 A32 32 0 1 0 82 50 L68 50 A18 18 0 1 1 50 32 Z" fill="#FFFFFF"/><circle cx="73" cy="50" r="7.5" fill="#5DADE2"/></svg>
      <div class="wm">Cost<span class="b2">Space</span></div>
      <p>Construction cost control and project financial management — one connected system for every riyal you spend.</p>
    </div>
    <div class="ft-col">
      <h4>Product</h4>
      <a href="#features">Features</a>
      <a href="#screens">Product tour</a>
      <a href="#pricing">Pricing</a>
      <a href="#catalogue">Catalogue</a>
      <a href="#pricing">Start free trial</a>
    </div>
    <div class="ft-col">
      <h4>Company</h4>
      <a href="#founder">Our story</a>
      <a href="#pricing">Start free trial</a>
      <a href="mailto:wessam.taha.ahmed@gmail.com">Contact</a>
    </div>
    <div class="ft-col">
      <h4>Modules</h4>
      <a href="#features">Budget Control</a>
      <a href="#features">Contracts</a>
      <a href="#features">Procurement</a>
      <a href="#features">Forecasting</a>
    </div>
    <div class="ft-col ft-contact">
      <h4>Contact</h4>
      <a href="mailto:wessam.taha.ahmed@gmail.com">wessam.taha.ahmed@gmail.com</a>
      <a href="tel:+201273556755">+20 127 355 6755 (Egypt)</a>
      <a href="tel:+966532742763">+966 53 274 2763 (Saudi)</a>
      <a href="https://wa.me/201273556755" target="_blank" rel="noopener">WhatsApp</a>
      <a href="https://www.linkedin.com/company/cost-space/" target="_blank" rel="noopener">LinkedIn — Cost Space</a>
      <a href="https://www.linkedin.com/company/cost-space/" target="_blank" rel="noopener" class="ft-qr-link">
        <img class="ft-qr" src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https%3A%2F%2Fwww.linkedin.com%2Fcompany%2Fcost-space%2F" alt="Scan to open CostSpace LinkedIn page" width="100" height="100" />
        <span class="ft-qr-cap">Scan for LinkedIn</span>
      </a>
    </div>
  </div>
  <div class="wrap ft-bot">
    <div class="c">© 2026 CostSpace. All rights reserved.</div>
  </div>
</footer>

` }} />
  );
}

export default CostSpaceLanding;
